import { ActionReducerMapBuilder, createSlice } from "@reduxjs/toolkit";
import { Organizer } from "../domain/interfaces/Organizer";
import { editTeam, createTeam, fetchOrganizer, resetOrganizer, upsertOrganizer } from "./actions";

type OrganizerSliceState = {
  isLoading: boolean;
  isLoaded: boolean;
  isTeamCreating: boolean;
  isTeamEditing: boolean;
  info: Organizer | null;
};

type OrganizerReducerBuilder = ActionReducerMapBuilder<OrganizerSliceState>;

const initialState: OrganizerSliceState = {
  isLoading: false,
  isLoaded: false,
  isTeamCreating: false,
  isTeamEditing: false,
  info: null,
};

function createFetchOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(fetchOrganizer.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchOrganizer.fulfilled, (state, action) => {
    const info = action.payload
      ? {
        ...action.payload,
        teams: action.payload.teams.sort((a, b) => b.id - a.id)
      }
      : action.payload;

    state.isLoading = false;
    state.isLoaded = true;
    state.info = info;
  });

  builder.addCase(fetchOrganizer.rejected, (state) => {
    state.isLoading = false;
    state.isLoaded = true;
  });
}

function createUpsertOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(upsertOrganizer.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(upsertOrganizer.fulfilled, (state, action) => {
    state.isLoading = false;
    state.isLoaded = true;
    state.info = action.payload;
  });

  builder.addCase(upsertOrganizer.rejected, (state) => {
    state.isLoading = false;
    state.isLoaded = true;
  });
}

function createResetOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(resetOrganizer, (state) => {
    state.isLoaded = false;
    state.isLoading = false;
    state.info = null;
  });
}

function createCreateOrganizerTeam(builder: OrganizerReducerBuilder) {
  builder.addCase(createTeam.pending, (state) => {
    state.isTeamCreating = true;
  });

  builder.addCase(createTeam.fulfilled, (state, { payload }) => {
    state.isTeamCreating = false;

    if (state.info) {
      const teams = [
        ...state.info.teams,
        payload,
      ].sort((a, b) => b.id - a.id);

      state.info = {
        ...state.info,
        teams,
      };
    }
  });

  builder.addCase(createTeam.rejected, (state) => {
    state.isTeamCreating = false;
  });
}

function createEditOrganizerTeam(builder: OrganizerReducerBuilder) {
  builder.addCase(editTeam.pending, (state) => {
    state.isTeamEditing = true;
  });

  builder.addCase(editTeam.fulfilled, (state, { payload }) => {
    state.isTeamEditing = false;

    if (state.info) {
      const editingTeam = state.info.teams.find((team) => team.id === payload.id);
      const editingTeamIndex = editingTeam ? state.info.teams.indexOf(editingTeam) : -1;

      if (editingTeamIndex !== -1) {
        state.info.teams.splice(editingTeamIndex, 1, payload);
        const teams = [
          ...state.info.teams
        ].sort((a, b) => b.id - a.id);

        state.info = {
          ...state.info,
          teams,
      };
    }
    }
  });

  builder.addCase(editTeam.rejected, (state) => {
    state.isTeamEditing = false;
  });
}

const slice = createSlice({
  name: 'organizer',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    createFetchOrganizerReducer(builder);
    createUpsertOrganizerReducer(builder);
    createResetOrganizerReducer(builder);
    createCreateOrganizerTeam(builder);
    createEditOrganizerTeam(builder);
  }
});

const organizerReducer = slice.reducer;
export default organizerReducer;
